// Hand-authored pixel sprites. Each sprite is a grid of palette chars; at load we bake
// them into crisp (nearest-filtered) textures. One shared palette keeps the whole set
// cohesive (neon-on-dark alchemy). White-ish sprites (proj/orb/shot/disc) are tinted at
// render time by their element color; entity sprites carry their own colors.

import { Graphics, Texture } from 'pixi.js';
import type { Renderer } from 'pixi.js';
import { Rng } from '../core/rng';
import { VARIANT } from '../game/variant';
import { SPRITES_HIDDEN } from './pixelArtHidden';

const T = -1; // transparent
const PAL: Record<string, number> = {
  '.': T,
  o: 0x140f1f, // outline / deep shadow
  w: 0xffffff,
  k: 0x2a2438, // stone mid
  K: 0x1b1730, // stone dark
  d: 0x0f0c1a, // stone crack
  F: 0x282340, // floor base (lifted out of pure black)
  h: 0x48406e, // tile seam highlight
  s: 0xffd9a0, // skin / light
  u: 0x8a5cff, // robe
  U: 0x5a34b0, // robe dark
  g: 0x7cfc00,
  G: 0x4f9c08, // acid
  r: 0xff5a6a,
  R: 0xb83a48, // slime
  a: 0xff9a3a,
  A: 0xc06a18, // spore
  b: 0x8ab4ff,
  B: 0x4f74c0, // glass
  p: 0xc06bff,
  P: 0x7e3fc0, // wraith
  n: 0x6ad06a,
  N: 0x3f8f3f, // vat
  e: 0xff3030,
  E: 0xb01818, // scorpion
  l: 0xb6ff5a,
  L: 0x79c020, // mortar
  c: 0x59ffd0, // mote
  y: 0xffe14d, // spark / glow
  m: 0xff4dff, // arcane
  i: 0xcfeeff, // ice / steam
  z: 0xc8c8f0, // construct metal
  q: 0x6e6d7e, // boulder light
  Q: 0x47454f, // boulder dark
  W: 0x4fb0e0, // water
  v: 0x8a82bc, // grass blade tip - lighter lavender (same purple family as the ground)
  V: 0x5e578e, // grass blade body
  t: 0x215b5b, // verdant teal
  T: 0x123b3c, // verdant teal shadow
  j: 0xb9e84f, // chartreuse ferment / leaf highlight
  J: 0x477b42, // deep vine green
  x: 0xa6f2dc, // pale mint glass rim
  X: 0x4a9d89, // mint shadow
  C: 0xc8873f, // copper
  D: 0x7a4628, // copper shadow
  H: 0xffc35a, // amber nectar
  M: 0xf35fca, // magenta boss bloom
  I: 0x2f8684, // irrigation water
  '0': 0xd0d5d5, // conduit steel highlight
  '1': 0x596065, // conduit steel shadow
  '2': 0x8cf6ff, // electric cyan
  '3': 0x2678a9, // cyan shadow
  '4': 0xf260ce, // starfall magenta
  '5': 0xffc266, // star-gold
  '6': 0xe55b4f, // meteor ember
  '7': 0xf59ab8, // ninja-dojo soft pink (squid / Ragam)
};

// prettier-ignore
const SPRITES: Record<string, string[]> = {
  // Player idle + two walk frames (legs/feet swap → walking motion).
  player: [
    '...ooo...',
    '..ouuuo..',
    '.ouuuuuo.',
    'ouuuuuuuo',
    '.ossssso.',
    '.osowoso.',
    '.osssso..',
    '.oUUUUo..',
    'oUuuuuUo.',
    '.UuuuuU..',
    '.oU.Uo...',
    '..o.o....',
  ],
  playerA: [
    '...ooo...',
    '..ouuuo..',
    '.ouuuuuo.',
    'ouuuuuuuo',
    '.ossssso.',
    '.osowoso.',
    '.osssso..',
    '.oUUUUo..',
    'oUuuuuUo.',
    '.UuuuuU..',
    '.oUUo....',
    '.o..o....',
  ],
  playerB: [
    '...ooo...',
    '..ouuuo..',
    '.ouuuuuo.',
    'ouuuuuuuo',
    '.ossssso.',
    '.osowoso.',
    '.osssso..',
    '.oUUUUo..',
    'oUuuuuUo.',
    '.UuuuuU..',
    '..oUUo...',
    '...o.o...',
  ],
  // NinjaKid Dojo hero skin: masked ninja in a black gi (teal headband + red scarf
  // carry visibility on the night arena). Same grid/silhouette as the frames above.
  ninjaPlayer: [
    '...ooo...',
    '..oKKKo..',
    '.occccco.',
    'oKKKKKKKo',
    '.oKKKKKo.',
    '.oKwKwKo.',
    '.oeeeeo..',
    '.oKKKKo..',
    'oKKeeKKo.',
    '.KKKKKK..',
    '.oK.Ko...',
    '..o.o....',
  ],
  ninjaPlayerA: [
    '...ooo...',
    '..oKKKo..',
    '.occccco.',
    'oKKKKKKKo',
    '.oKKKKKo.',
    '.oKwKwKo.',
    '.oeeeeoe.',
    '.oKKKKo..',
    'oKKeeKKo.',
    '.KKKKKK..',
    '.oKKo....',
    '.o..o....',
  ],
  ninjaPlayerB: [
    '...ooo...',
    '..oKKKo..',
    '.occccco.',
    'oKKKKKKKo',
    '.oKKKKKo.',
    '.oKwKwKo.',
    '.oeeeeo..',
    '.oKKKKo..',
    'oKKeeKKo.',
    '.KKKKKK..',
    '..oKKo...',
    '...o.o...',
  ],
  slime: [
    '..oooo..',
    '.orrrro.',
    'orrrrrro',
    'orwrrwro',
    'orrrrrro',
    'orRRRRro',
    'oRRRRRRo',
    '.oooooo.',
  ],
  spore: [
    'o..o..o.',
    '.oaaao.o',
    'oaaaaao.',
    'oawaawo.',
    'oaaaaao.',
    '.oAAAo..',
    'o.ooo.o.',
    '.o...o..',
  ],
  glass: [
    '...bb...',
    '..bwwb..',
    '.bwbbwb.',
    'bbbBBbbb',
    '.bBBBBb.',
    '..bBBb..',
    '...bb...',
    '........',
  ],
  wraith: [
    '..oooo..',
    '.oppppo.',
    'opwppwpo',
    'oppppppo',
    'oppppppo',
    'oPpPpPpo',
    'oP.P.P.o',
    '.o.o.o..',
  ],
  vat: [
    '.oooooo.',
    'oNNNNNNo',
    'oNnnnnNo',
    'onnwwnno',
    'onnnnnno',
    'oNnnnnNo',
    'oNNNNNNo',
    '.oooooo.',
  ],
  scorpion: [
    'o.o....yo',
    'eEo...oyo',
    'oeeeeeoy.',
    'eeeeeeeo.',
    'oeeEEeeo.',
    'oeo..oeo.',
    'o.....o..',
    '.........',
  ],
  mortar: [
    '..ooo...',
    '.oLLLo..',
    'olllllo.',
    'olwllwo.',
    'ollllo..',
    '.oLLo...',
    '.olllo..',
    '..ooo...',
  ],
  flask: [
    '....oooo....',
    '....owwo....',
    '....onno....',
    '...onnno....',
    '..onnnnno...',
    '.onnNNnnno..',
    'onnNwwNnnno.',
    'onNNwwNNno..',
    'onnNNNNnno..',
    '.onnNNnno...',
    '..oooooo....',
    '............',
  ],
  construct: [
    '..oo..oo..',
    '.ozzoozzo.',
    'ozzzzzzzzo',
    'ozwzzzzwzo',
    'ozzzzzzzzo',
    'ozZZ..ZZzo',
    'ozzzzzzzzo',
    'oz.zzzz.zo',
    '.ozo..ozo.',
    '..o....o..',
  ],
  catalyst: [
    '...oooo...',
    '..ommmmo..',
    '.ommmmmmo.',
    'ommwmmwmmo',
    'ommmmmmmmo',
    'ommmwwmmmo',
    'omPmmmmPmo',
    '.ommmmmmo.',
    '..ommmmo..',
    '...oooo...',
  ],
  // Verdant Still creature family. These stay deliberately compact, high-contrast, and
  // hand-authored on the exact same one-logical-pixel grid as the Base Lab sprites.
  yeastling: [
    '..oooo..',
    '.ojjjjo.',
    'ojjjjjjo',
    'ojxjjxjo',
    'ojjjjjjo',
    'oJJjjJJo',
    '.oJJJJo.',
    '..oooo..',
  ],
  glassGnat: [
    '...x.x...',
    '..xttxx..',
    '.xtjjtxx.',
    'xtjxxjtxx',
    '.xjttjx..',
    '..xJJx...',
    '...oo....',
  ],
  rootling: [
    '....j....',
    '...jJj...',
    '..jJJJj..',
    '.oJxJxJo.',
    'oJJjjjJJo',
    '.oJjJjJo.',
    '..oJ.Jo..',
    '.o..J..o.',
  ],
  kegCrab: [
    '..oooooo..',
    '.oCCCCCCo.',
    'oCDCCCCDCo',
    'oCxxCCxxCo',
    'oCCCCCCCCo',
    '.oDCDCDDDo',
    'o.o....o.o',
    '.o......o.',
  ],
  mycelialSlinger: [
    '...MMMM...',
    '..MmmmmM..',
    '.MmMmmMmM.',
    'oMmxmmxmMo',
    'oMmmmmmmMo',
    '.oTTTTTTo.',
    '..oT.TTo..',
    '.o..T..o..',
  ],
  copperWasp: [
    '.x...x.',
    'xXC.CXx',
    '.oCCCo.',
    'oCxxCCo',
    '.oCDCo.',
    '..o.o..',
    '.o...o.',
  ],
  bloomBrute: [
    '...oooo...',
    '..oJJJJo..',
    '.oJjjjjJo.',
    'oJjxjjxJJo',
    'oJjjJJjjJo',
    'oJJJCCJJJo',
    '.oJJJJJJo.',
    '..oJ..Jo..',
    '.o......o.',
  ],
  vatmother: [
    '....oooo....',
    '...oJJJJo...',
    '..oJjjjjJo..',
    '.oJjJxxJjJo.',
    'oJjjjjjjjjJo',
    'oJjJJCCJJjJo',
    'oJjjCCCCCjjJo',
    '.oJjCCCCCjJo.',
    '..oJJJJJJo..',
    '...oooooo...',
  ],
  copperhorn: [
    'oC...C...Co',
    '.oC.CC.CCo.',
    '..oCCCCo...',
    '.oCxxxxCCo.',
    'oCxxCCxxCCo',
    'oCCCCCCCCCo',
    '.oCDCDDCo..',
    '..oC...Co..',
  ],
  verdantAlembic: [
    '....oooo....',
    '...oMMMMo...',
    '..oMmmmmMo..',
    '.oMmMxxMmMo.',
    'oMmmmmmmmmMo',
    'oMmJJCCJJmMo',
    'oMmJCCCCJmMo',
    '.oMmJCCJmMo.',
    '..oMMMMMMo..',
    '...oJ..Jo...',
  ],
  // Verdant terrain, hazards, and pickups.
  copperStill: [
    '....ooooo....',
    '..ooCCCCCoo..',
    '.oCCHHHHCCo..',
    'oCCHDDDDHCCo.',
    'oCHDCCCCDHCCo',
    'oCHDCHHCDHCCo',
    'oCHDCCCCDHCCo',
    'oCCHDDDDHCCo.',
    '.oCCHHHHCCo..',
    '..ooCCCCCoo..',
    '....ooooo....',
  ],
  vineWall: [
    '..oJxo..',
    '.oJjjJo.',
    'oJxJJxJo',
    'oJjjjjJo',
    'oJJxJJJo',
    '.oJjjJo.',
    '..oJJo..',
  ],
  sporeShot: ['..w.w..', '.w.www.', 'wwwwwww', '.wwwww.', '..www..'],
  nectar: ['..HH..', '.HwwH.', 'HwwwwH', '.HwwH.', '..HH..'],
  healthOrb: [
    '...ccc...',
    '..cgggc..',
    '.cggwggc.',
    'cggwwwggc',
    'cwwwwwwwc',
    'cggwwwggc',
    '.cggwggc.',
    '..cgggc..',
    '...ccc...',
  ],
  catalystPool: ['...HHHHH...', '.HHwwwwwHH.', 'HHwwHHHwwHH', 'HwwHHHHHwwH', 'HHwwHHHwwHH', '.HHwwwwwHH.', '...HHHHH...'],
  irrigation0: ['...IIIII...', '..xIIIIII..', '.IIIIIIIII.', 'IIIIIIIIIII', '.IIIIIIIII.', '..IIIIII...', '...IIIII...'],
  irrigation1: ['...IIIIII...', '.xIIIIIIIIx.', 'IIIIIIIIIII.', '.IIIIIIIII.', '..IIIIIII..', '...IIIII...'],
  irrigation2: ['..IIII...', '.xIIIII..', 'IIIIIII..', '.IIIIIII.', '..IIIIII.', '...IIII..'],
  irrigation3: ['..III.III..', '.xIIIIIIIx.', 'IIIIIIIII..', '.IIIIIII...', '..IIIII....'],
  root0: ['....j....', '..jJj.j..', '.jJJJj...', '...JJj...', '....J....', '...oJo...'],
  root1: ['.........', '..jjj....', '.jJJJj...', 'jJ..JJj..', '.jJJJj...', '..oJo....'],
  root2: ['..xxx....', '.xjjx.jj.', '..JJ.jJJj', '..J...J..', '.oJ...Jo.'],
  verdantAltar: [
    '..ooooooooooo..',
    '.oJJJJJJJJJJJo.',
    'oJTTTTTTTTTTTJo',
    'oJT.........TJo',
    'oJT..HHHHH..TJo',
    'oJT.HH.w.HH.TJo',
    'oJT.H.wwH.H.TJo',
    'oJT.HH.w.HH.TJo',
    'oJT..HHHHH..TJo',
    'oJT.........TJo',
    'oJTTTTTTTTTTTJo',
    '.oJJJJJJJJJJJo.',
    '..ooooooooooo..',
  ],
  // Starfall Conduit enemy family. The same one-pixel rendering language is retained,
  // but silhouettes are mechanical, many-legged, aerial, or architectural rather than
  // recolored versions of Base Lab creatures.
  coalMite: [
    '..o.......o..', 'o..o.....o..o', '.o..ooooo..o.', '..o1666661o..', '.o16w66w61o.', 'o.o1666661o.o', '...o11111o...', '..o..o.o..o..',
  ],
  prismDrifter: [
    '......2......', '....23232....', '..2232w2322..', '2233wwwww3322', '..2232w2322..', '....23.32....', '...3.....3...',
  ],
  railSentinel: [
    '...222...', '..20502..', '.o20102o.', 'o0012100o', 'o0122210o', 'o01w2w10o', 'o0111110o', '..o111o..', '...o1o...', '...o1o...', '..o...o..',
  ],
  kilnHound: [
    '......oo.....', '..oooo66oo...', '.o65566666o..', 'o65w666661o..', 'o6666611111o.', '.o611o...o1o.', 'o.o..o...o..o', '.o...o...o...',
  ],
  starScribe: [
    '....444....', '...4w2w4...', '..o44444o..', '.o4011040o.', 'o401221040o', '.o4011104o.', '..o41114o..', '...o1o.....', '..o.1.o....', '.o..1..o...',
  ],
  magnetBeetle: [
    'o5.........5o', '.o5.......5o.', '..o.ooooo.o..', '.o011111110o.', 'o0122222210o', 'o012w22w210o', '.o01222210o.', 'o3o.01110.o3o', '.o..o...o..o.',
  ],
  conduitShard: [
    '...2...', '..232..', '.23032.', '230w032', '.23032.', '..232..', '...2...',
  ],
  conduitGolem: [
    '...oooo...', '..o011o..', '.o012210o.', 'o01222210o', 'o01w22w10o', 'o01222210o', 'o01111110o', '.o11..11o.', 'o3o....o3o', '.o......o.',
  ],
  railwright: [
    '....oooo....', '...o0550o...', '..o055550o..', '.o05122150o.', 'o0512222150o', 'o05w2222w50o', 'o0512222150o', '.o05111150o.', '..o011110o..', '...o1..1o...',
  ],
  meteorHart: [
    'o5...5...5o', '.o5.55.55o.', '..o55555o...', '.o566ww665o.', 'o5666666665o', 'o5611551165o', '.o56111165o.', '..o5....5o..',
  ],
  starfallEngine: [
    '....oooo....', '...o4444o...', '..o422224o..', '.o42222224o.', 'o422w22w224o', 'o4222222224o', 'o4222552224o', '.o42222224o.', '..o411114o..', '...o4..4o...',
  ],
  // NinjaKid Dojo creature family - drawn from the kid's sketchbook page: household
  // objects gone bad, classic monsters, and his seven bosses, in his colors.
  evilChair: [
    '.ooooo..', 'oqqqqqo.', 'oqwqwqo.', 'oqqqqqo.', 'oqqqqqo.', 'oQQQQQo.', 'oqqqqQo.', 'oQo.oQo.', 'oQo.oQo.', '.o...o..',
  ],
  mosquito: [
    'w.....w.', '.w...w..', '.opppo..', 'opPwPpo.', '.opppo..', '..op.o..', '...o....',
  ],
  penguin: [
    '..oooo..', '.oKKKKo.', 'oKwKKwKo', 'oKKaaKKo', 'oKwwwwKo', 'oKwwwwKo', 'oKwwwwKo', '.oKKKKo.', '..a..a..',
  ],
  snake: [
    '..oooo...', '.onnnno..', '.onwnwo..', '..onnnno.', '....onnno', '..onnnno.', '.onnnno..', '..oooo...',
  ],
  snakelet: [
    '.ooo..', 'onnno.', 'onwno.', '.onnno', '..onno', '...oo.',
  ],
  squid: [
    '..ooooo..', '.o77777o.', 'o7777777o', 'o77w7w77o', 'o7777777o', '.o77777o.', '.7.7.7.7.', '.7.7.7.7.',
  ],
  evilHoover: [
    '..oooooo..o.', '.obbbbbbo.Bo', 'obbwbbwbboB.', 'obbbbbbbboB.', 'obBBBBBBbBo.', 'obbbbbbbbo..', '.obbbbbbo...', '..oBo.oBo...', '...o...o....',
  ],
  cerbere: [
    '.oo.oo.oo..', 'oCCoCCoCCo.', 'oCwoCwoCwo.', '.oCCCCCCCo.', '.oCCCCCCo..', '.oCDDDCCo..', '.oCo..oCo..', '..o....o...',
  ],
  giantSpider: [
    'o..o.....o..o', '.o.o.....o.o.', '..ooIIIIIoo..', '.oIIwIIIwIIo.', '..oIIIIIIIo..', '.o.oIIIIIo.o.', 'o..o.ooo.o..o', '...o.....o...',
  ],
  dragon: [
    '..oo....oo..', '.oGGo..oGGo.', '..oGGooGGo..', '..ogggggggo.', '.ogwggggggo.', '.ogggggggo..', '..oggo.oggo.', '...oo...oo..',
  ],
  centaurus: [
    '....oo.....', '...osso....', '...osso....', '..oCCCoooo.', '.oCCCCCCCo.', '.oCCCCCCCo.', '.oCDCCDCCo.', '.oCo..oCo..', '.oCo..oCo..', '..o....o...',
  ],
  ciclopus: [
    '..ooooo....', '.onnnnno...', 'onnnnnnno..', 'onnwwwnno..', 'onnwewnno..', 'onnwwwnno..', 'onnnnnnno..', '.onnnnno...', '.ono..ono..', '..o....o...',
  ],
  trole: [
    '..oooo...y.', '.onnnno.oyo', 'onnwnwno.yo', 'onnnnnno.y.', 'onnnnnnoo..', '.onNNNno...', '.onnnnno...', '.ono.ono...', '.ono.ono...', '..o...o....',
  ],
  goldEagle: [
    'o.........o', 'oy.......yo', '.oyy...yyo.', '..oyyyyyo..', '..oy5w5yo..', '...oyyyo...', '....oyo....', '.....a.....',
  ],
  rinocerite: [
    '...ooooooo...', '..oqqqqqqqo..', '.oqqqqqqqqqo.', 'oqqwqqqqqqqo.', 'oqqqqqqqqqoww', 'oqQQqqqQQqo..', '.oqo...oqo...', '.oqo...oqo...', '..o.....o....',
  ],
  // The seven bosses, in the kid's sketch order.
  dakne: [
    '.o........o.', 'oeo......oeo', '.oeeooooeeo.', '..oeeeeeeo..', '.oeeweeweeo.', '.oeeeeeeeeo.', '.oeEEeeEEeo.', '..oeeeeeeo..', '..oeo..oeo..', '...o....o...',
  ],
  collector: [
    '...oooooo...', '..oMMMMMMo..', '.oMMwMMwMMo.', 'oMMMooooMMMo', 'oMMoKKKKoMMo', 'oMMoKKKKoMMo', 'oMMMooooMMMo', '.oMMMMMMMMo.', '..oMMMMMMo..', '...oooooo...',
  ],
  supremeVlad: [
    '....oooo....', '...oKKKKo...', '.oooooooooo.', 'oKKKKKKKKKKo', '..osssssso..', '..osesseso..', '..oswsswso..', '.ouuuuuuuuo.', 'ouuUuuuuUuuo', 'ouuuuuuuuuuo', '.oUUUUUUUUo.', '..oooooooo..',
  ],
  odin: [
    '.y..oooo..y.', 'yyoqqqqqqoyy', '.yoqqqqqqoy.', '..oswsosso..', '..owwwwwwo..', '.owwwwwwwwo.', '.onnnnnnnno.', 'onnNnnnnNnno', '.onnnnnnnno.', '..ono..ono..', '...o....o...',
  ],
  mosquitaro: [
    'w....w.w....w', '.w..w...w..w.', '..opppppppo..', '.opPpppppPpo.', 'opppwpppwpppo', '.opppppppppo.', '..opppppppo..', '....op.po....', '.....o.o.....', '.....o.o.....',
  ],
  ragam: [
    '.oo......oo.', 'o77o....o77o', 'o777oooo777o', '.o77777777o.', '.o7w7777w7o.', '.o77777777o.', '.o77woow77o.', '..o777777o..', '..o7o..o7o..', '...o....o...',
  ],
  terence: [
    '...oooooo...', '..oIIIIIIo..', '..occcccco..', '.oIIwIIwIIo.', '.oIIIIIIIIo.', '.oIXXIIXXIo.', '.oIIIIIIIIo.', '..oIIIIIIo..', '..oIo..oIo..', '...o....o...',
  ],
  // Ninja weapon projectiles (white; tinted by the weapon color at render time).
  projectileKunai: [
    '..ww.....', '.wwww....', '..wwwww.w', '.wwww..w.', '..ww.....',
  ],
  windShuriken: [
    'w.........w', 'ww.......ww', '.ww.....ww.', '..ww.w.ww..', '...wwwww...', '....w.w....', '...wwwww...', '..ww.w.ww..', '.ww.....ww.', 'ww.......ww', 'w.........w',
  ],
  projectileHound: [
    '.ww...w.', 'wwww.ww.', 'wwwwwww.', '.wwwwww.', '.ww..ww.', '.w....w.',
  ],
  // Terrain, environmental accents and projectiles for the Conduit.
  steelPylon: [
    '....ooooo....',
    '..oo11111oo..',
    '.o110000011o.',
    'o11005550011o',
    'o10052225001o',
    'o10522w22501o',
    'o10052225001o',
    'o11005550011o',
    '.o111000111o.',
    '..oo11111oo..',
    '....ooooo....',
  ],
  railPylon: [
    '...ooo...',
    '..o555o..',
    '.o50105o.',
    'o5012105o',
    'o5012105o',
    '.o50105o.',
    '..o111o..',
    '...o.o...',
  ],
  railArrow: ['2.....2', '.2...2.', '..2.2..', '...2...', '...2...'],
  starShot: ['..2.2..', '.24w42.', '24www42', '.4www4.', '..2w2..'],
  prismBolt: ['..2....', '.232...', '23w32..', '.232...', '..2....'],
  auraCloud: [
    '...www...',
    '.wwwwwww.',
    'wwwwwwwww',
    'wwwwwwwww',
    '.wwwwwww.',
    '..wwwww..',
  ],
  abilitySpark: ['..w..', '.www.', 'wwwww', '.www.', '..w..'],
  statusFlame: [
    '...w...',
    '..ww...',
    '..www..',
    '.wwww..',
    '.wwwww.',
    'wwwwwww',
    '.wwwww.',
    '..www..',
  ],
  abilityShield: [
    '..www..',
    '.wwwww.',
    'wwwwwww',
    'wwwwwww',
    '.wwwww.',
    '.wwwww.',
    '..www..',
    '...w...',
  ],
  abilityWind: [
    '.........',
    '.wwww....',
    '.....ww..',
    '..wwwwwww',
    'ww.......',
    '.wwwwww..',
    '.........',
  ],
  abilityVoid: [
    '..www..',
    '.w...w.',
    'w.....w',
    'w..w..w',
    'w.....w',
    '.w...w.',
    '..www..',
  ],
  abilityBolt: [
    '....ww.',
    '...ww..',
    '..www..',
    '.wwww..',
    '...ww..',
    '..ww...',
    '.ww....',
    'ww.....',
  ],
  statusRipple: ['..wwwww..', '.ww...ww.', 'ww.....ww'],
  statusIceShard: ['..w..', '.www.', 'wwwww', '.www.', '..w..', '..w..'],
  statusThorn: [
    '...w...',
    'w..w..w',
    '.w.www.',
    '..www..',
    '.wwwww.',
    'w..w..w',
    '...w...',
  ],
  statusRoot: [
    '...w...',
    '..www..',
    '...w...',
    '.w.w.w.',
    'w..w..w',
    '.w.w.w.',
    'w.....w',
    'w.....w',
  ],
  statusSpore: [
    '..www..',
    '.wwwww.',
    'wwwwwww',
    'www.www',
    'wwwwwww',
    '.wwwww.',
    '..w.w..',
  ],
  statusFirefly: ['w..w..w', '.wwwww.', '..www..', '.wwwww.', 'w..w..w'],
  statusClock: [
    '..wwwww..',
    '.ww...ww.',
    'ww..w..ww',
    'w...w...w',
    'w...www.w',
    'w.......w',
    'ww.....ww',
    '.ww...ww.',
    '..wwwww..',
  ],
  statusSatellite: [
    'ww.....ww',
    'www...www',
    '..wwwww..',
    '...www...',
    '..wwwww..',
    'www...www',
    'ww.....ww',
  ],
  statusRail: ['ww.ww.ww.', 'ww.ww.ww.', 'wwwwwwwww', '.ww.ww.ww'],
  projectileSap: [
    '...w...',
    '..www..',
    '.wwwww.',
    'wwwwwww',
    'wwwwwww',
    '.wwwww.',
    '..www..',
  ],
  projectileHop: [
    '..www..',
    '.wwwww.',
    'www.www',
    '.wwwww.',
    '..www..',
    '...w...',
    '..w.w..',
  ],
  projectileThorn: [
    '......w',
    '....www',
    '..wwwww',
    'wwwwwww',
    '..wwwww',
    '....www',
    '......w',
  ],
  projectileComet: [
    'ww.......',
    '.www.....',
    '..wwwww..',
    '...wwwwww',
    '..wwwww..',
    '.www.....',
    'ww.......',
  ],
  orbitSeed: [
    '..www..',
    '.wwwww.',
    'wwwwwww',
    'www.www',
    'wwwwwww',
    '.wwwww.',
    '..www..',
  ],
  coolant0: ['...11111...', '..1333331..', '.133232331.', '13333333331', '.133323331.', '..1333331..', '...11111...'],
  coolant1: ['...111111...', '.1333333331.', '13332333331', '.1333332331.', '..1333331..', '...11111...'],
  coolant2: ['..1111...', '.133331..', '1332331..', '.1333331.', '..133331.', '...1111..'],
  coolant3: ['..111.111..', '.133333331.', '133323331..', '.1333331...', '..11111....'],
  forge0: ['..........', '..DD......', '.DCCDD....', 'DCCCCDoo..', '.D..DCo...', '....oo....'],
  forge1: ['..oooo...', '.o0000o..', 'o011110o.', 'o01C110o.', 'o011110o.', '.o0000o..', '..oooo...'],
  forge2: ['....2....', '...232...', '..23032..', '.230w032.', '..23032..', '...303...', '....o....'],
  conduitAltar: [
    '..ooooooooooo..', '.o01111111110o.', 'o0111111111110o', 'o01.........10o', 'o01..22222..10o', 'o01.22.w.22.10o', 'o01.2.2w2.2.10o', 'o01.22.w.22.10o', 'o01..22222..10o', 'o01.........10o', 'o0111111111110o', '.o01111111110o.', '..ooooooooooo..',
  ],
  // White/light sprites - tinted by the weapon color at render time.
  // proj is an elongated capsule; the renderer rotates it to its travel direction.
  proj: ['..wwww..', 'wwwwwwww', '..wwww..'],
  orb: ['.www.', 'wwwww', 'wwwww', 'wwwww', '.www.'],
  shot: ['.w.w.', 'w.w.w', '.www.', 'w.w.w', '.w.w.'],
  mote: ['.cc.', 'cwwc', 'cwwc', '.cc.'],
  // 3x5 pixel digits for damage numbers (tinted at render; avoids font generation).
  d0: ['www', 'w.w', 'w.w', 'w.w', 'www'],
  d1: ['.w.', 'ww.', '.w.', '.w.', 'www'],
  d2: ['www', '..w', 'www', 'w..', 'www'],
  d3: ['www', '..w', 'www', '..w', 'www'],
  d4: ['w.w', 'w.w', 'www', '..w', '..w'],
  d5: ['www', 'w..', 'www', '..w', 'www'],
  d6: ['www', 'w..', 'www', 'w.w', 'www'],
  d7: ['www', '..w', '..w', '..w', '..w'],
  d8: ['www', 'w.w', 'www', 'w.w', 'www'],
  d9: ['www', 'w.w', 'www', '..w', 'www'],
  // Half-buried boulder (lighter top, darker buried base).
  boulder: [
    '....oooo....',
    '..ooqqqqoo..',
    '.oqqqqqqqqo.',
    'oqqqwqqqqqqo',
    'oqqqqqqqqqqo',
    'oQQQqqqQQQQo',
    'oQQQQQQQQQQo',
    '.oQQQQQQQQo.',
    '..oooooooo..',
  ],
  // Water puddles (translucent at render time): four irregular blobs with an asymmetric
  // edge-sheen (`i`) on one side - no symmetric highlights, so they don't look like faces.
  puddle0: [
    '...WWWWWW...',
    '..iWWWWWWWW.',
    '.iWWWWWWWWWW',
    'WWWWWWWWWWWW',
    'WWWWWWWWWWW.',
    '.WWWWWWWWWW.',
    '.WWWWWWWWW..',
    '..WWWWWWW...',
    '...WWWWW....',
  ],
  puddle1: [
    '....WWWWWW....',
    '..WWWWWWWWWW..',
    '.iWWWWWWWWWWW.',
    'iWWWWWWWWWWWWW',
    'WWWWWWWWWWWWWW',
    'WWWWWWWWWWWWW.',
    '.WWWWWWWWWWWW.',
    '.WWWWWWWWWWW..',
    '..WWWWWWWWW...',
    '....WWWWWW....',
  ],
  puddle2: [
    '...WWWW...',
    '..WWWWWW..',
    '.iWWWWWWW.',
    '.WWWWWWWW.',
    'WWWWWWWWWW',
    'WWWWWWWWWW',
    'WWWWWWWWW.',
    '.WWWWWWWW.',
    '.WWWWWWW..',
    '..WWWWW...',
    '...WWW....',
  ],
  puddle3: [
    '..WWW.WWWW..',
    '.iWWWWWWWWW.',
    'iWWWWWWWWWWW',
    'WWWWWWWWWWWW',
    '.WWWWWWWWWW.',
    '..WWWWWWWW..',
    '...WWWWWW...',
    '....WWW.....',
  ],
  drop: ['.i.', '.i.', 'iwi', '.i.'], // light cyan-white so it pops on dark ground
  // Cosmetic grass: small fans of thin blades (lighter tips → darker base), like a tuft.
  grass0: [
    'v..v..v',
    '.v.v.v.',
    '.v.v.v.',
    '..vvv..',
    '..VVV..',
    '...V...',
  ],
  grass1: [
    'v.v...v.v',
    '.v.v.v.v.',
    '.v.v.v.v.',
    '..vVvVv..',
    '...VVV...',
    '....V....',
  ],
  grass2: [
    'v.v.v.v.v.v',
    '.v.v.v.v.v.',
    '..v.v.v.v..',
    '...vVvVv...',
    '....VVV....',
    '.....V.....',
  ],
  // Healing altar (top-down stone dais with a teal glow + white heal cross).
  altar: [
    '..ooooooooooo..',
    '.oqqqqqqqqqqqo.',
    'oqQQQQQQQQQQQqo',
    'oqQ.........Qqo',
    'oqQ..ccccc..Qqo',
    'oqQ.cc.w.cc.Qqo',
    'oqQ.c.www.c.Qqo',
    'oqQ.cc.w.cc.Qqo',
    'oqQ..ccccc..Qqo',
    'oqQ.........Qqo',
    'oqQQQQQQQQQQQqo',
    '.oqqqqqqqqqqqo.',
    '..ooooooooooo..',
  ],
};

// The ground is a procedurally-generated organic stone texture (fine speckled grain,
// no hard grid, tiles seamlessly) - see bakeGround(). The macro "lighter center → darker
// edges" look comes from the screen-space vignette, not the ground itself.

// Organic stone-ground tile: a base fill peppered with low-contrast speckles in nearby
// shades. Random speckles wrap, so it tiles without visible seams; the grain reads as a
// cohesive surface rather than distinct objects.
function bakeGround(): Texture {
  const size = 128;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (ctx) {
    const rng = new Rng(20240624);
    ctx.fillStyle = '#322c54';
    ctx.fillRect(0, 0, size, size);
    const shades = ['#3a3463', '#2b2649', '#383158', '#262143', '#403a6c'];
    const speckles = Math.floor(size * size * 0.22);
    for (let i = 0; i < speckles; i++) {
      ctx.fillStyle = shades[rng.int(0, shades.length)];
      ctx.fillRect(rng.int(0, size), rng.int(0, size), 1, 1);
    }
  }
  const tex = Texture.from(c);
  tex.source.scaleMode = 'nearest';
  tex.source.addressMode = 'repeat';
  return tex;
}

// Overgrown cellar stone. The texture keeps Base Lab's fine grain, then adds small,
// irregular moss islands and broken cracks. Nothing runs across the full tile, so the
// repeated texture reads as old masonry instead of a square overlay grid.
function bakeVerdantGround(): Texture {
  const size = 128;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (ctx) {
    const rng = new Rng(20260711);
    ctx.fillStyle = '#273d35';
    ctx.fillRect(0, 0, size, size);
    const shades = ['#2d473c', '#22362f', '#30493e', '#1e332d', '#35483b'];
    for (let i = 0; i < 3300; i++) {
      ctx.fillStyle = shades[rng.int(0, shades.length)];
      ctx.fillRect(rng.int(0, size), rng.int(0, size), 1, 1);
    }

    const moss = ['#36533c', '#446447', '#506f49', '#294a38'];
    for (let i = 0; i < 110; i++) {
      ctx.fillStyle = moss[rng.int(0, moss.length)];
      ctx.fillRect(rng.int(1, size - 1), rng.int(1, size - 1), 1, 1);
    }

    for (let crack = 0; crack < 14; crack++) {
      let x = rng.int(3, size - 4);
      let y = rng.int(3, size - 4);
      ctx.fillStyle = rng.chance(0.5) ? '#172922' : '#1b2e28';
      for (let step = 0; step < rng.int(3, 8); step++) {
        ctx.fillRect(x, y, 1, 1);
        x += rng.int(-1, 2);
        y += rng.int(0, 2);
      }
    }

    ctx.fillStyle = '#8b5a32';
    for (let i = 0; i < 14; i++) ctx.fillRect(rng.int(2, size - 2), rng.int(2, size - 2), 1, 1);
  }
  const tex = Texture.from(c);
  tex.source.scaleMode = 'nearest';
  tex.source.addressMode = 'repeat';
  return tex;
}

// Weathered iron observatory decking. Its neutral charcoal, steel flakes, rust chips,
// and cold inlays keep it visually separate from Base Lab's purple stone floor.
function bakeConduitGround(): Texture {
  const size = 128;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (ctx) {
    const rng = new Rng(20260712);
    ctx.fillStyle = '#262b2e';
    ctx.fillRect(0, 0, size, size);
    const shades = ['#30363a', '#1d2225', '#34383a', '#21272a', '#3a3430'];
    for (let i = 0; i < 3000; i++) {
      ctx.fillStyle = shades[rng.int(0, shades.length)];
      ctx.fillRect(rng.int(0, size), rng.int(0, size), 1, 1);
    }

    for (let crack = 0; crack < 36; crack++) {
      let x = rng.int(4, size - 5);
      let y = rng.int(4, size - 5);
      ctx.fillStyle = rng.chance(0.55) ? '#111517' : '#181d20';
      for (let step = 0; step < rng.int(3, 7); step++) {
        ctx.fillRect(x, y, 1, 1);
        x += rng.int(-1, 2);
        y += rng.int(-1, 2);
      }
    }

    const metal = ['#75543b', '#9a7044', '#b1844d'];
    for (let fitting = 0; fitting < 4; fitting++) {
      const x = rng.int(3, size - 5);
      const y = rng.int(3, size - 5);
      ctx.fillStyle = metal[rng.int(0, metal.length)];
      ctx.fillRect(x, y, rng.chance(0.25) ? 2 : 1, 1);
    }

    const inlays = ['#356b7c', '#43899b', '#65b5bf'];
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = inlays[rng.int(0, inlays.length)];
      ctx.fillRect(rng.int(3, size - 3), rng.int(3, size - 3), 1, 1);
    }
  }
  const tex = Texture.from(c);
  tex.source.scaleMode = 'nearest';
  tex.source.addressMode = 'repeat';
  return tex;
}

function bakeCircle(renderer: Renderer, radius: number): Texture {
  const g = new Graphics().circle(radius, radius, radius).fill(0xffffff);
  const tex = renderer.generateTexture({ target: g, resolution: 1 });
  tex.source.scaleMode = 'nearest';
  g.destroy();
  return tex;
}

function bake(renderer: Renderer, rows: string[]): Texture {
  const h = rows.length;
  const w = rows[0].length;
  const g = new Graphics();
  // Transparent full-grid rect fixes the texture bounds to the whole grid.
  g.rect(0, 0, w, h).fill({ color: 0x000000, alpha: 0 });
  for (let y = 0; y < h; y++) {
    const row = rows[y];
    for (let x = 0; x < row.length; x++) {
      const col = PAL[row[x]];
      if (col !== undefined && col !== T) g.rect(x, y, 1, 1).fill(col);
    }
  }
  const tex = renderer.generateTexture({ target: g, resolution: 1 });
  tex.source.scaleMode = 'nearest'; // crisp pixels
  g.destroy();
  return tex;
}

// A soft radial vignette (transparent center → dark edges) for mood/focus.
function bakeVignette(): Texture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(128, 128, 40, 128, 128, 134);
    grad.addColorStop(0, 'rgba(8,6,16,0)');
    grad.addColorStop(1, 'rgba(8,6,16,0.62)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 256);
  }
  const tex = Texture.from(c);
  tex.source.scaleMode = 'linear'; // smooth gradient (not pixelated)
  return tex;
}

export interface Art {
  readonly textures: Record<string, Texture>;
  readonly stone: Texture;
  readonly verdant: Texture;
  readonly conduit: Texture;
  readonly vignette: Texture;
}

// Sprite set for this page: base art, with the hidden variant's overrides merged in on
// /hidden (keys not overridden fall through to the base sprite).
function activeSprites(): Record<string, string[]> {
  return VARIANT === 'hidden' ? { ...SPRITES, ...SPRITES_HIDDEN } : SPRITES;
}

export function buildArt(renderer: Renderer): Art {
  const textures: Record<string, Texture> = {};
  const set = activeSprites();
  for (const key in set) textures[key] = bake(renderer, set[key]);
  textures.disc = bakeCircle(renderer, 16); // soft round disc for aura + particles
  return {
    textures,
    stone: bakeGround(),
    verdant: bakeVerdantGround(),
    conduit: bakeConduitGround(),
    vignette: bakeVignette(),
  };
}

// Avatar palette: [hat/robe light, dark] pairs. The base alchemist sprite is recolored
// into these for the pickable leaderboard avatars (index 0 = the default purple wizard).
// prettier-ignore
export const AVATAR_COLORS: ReadonlyArray<readonly [number, number]> = [
  [0x8a5cff, 0x5a34b0], [0xff5a6a, 0xb83a48], [0x4f9cff, 0x2f5fb0], [0x5fc25f, 0x3a8a3a],
  [0xff9a3a, 0xc06a18], [0x4fd6e0, 0x2f95a0], [0xff7ad0, 0xc04a90], [0xe0c040, 0xa08820],
  [0x3fc0a0, 0x208070], [0xd02040, 0x901530], [0x6a5cff, 0x4030b0], [0x9ad040, 0x6a9020],
  [0xff4dff, 0xb030b0], [0x6ac0ff, 0x3a80c0], [0xb07040, 0x704825], [0x90a0c0, 0x5a6a8a],
  [0xff8a8a, 0xc05a5a], [0x40c070, 0x208048], [0xffc04d, 0xc08820], [0xb070ff, 0x7040c0],
];
export const AVATAR_COUNT = AVATAR_COLORS.length;

/** Draw the alchemist recolored to avatar `index` into a crisp DOM canvas. */
export function avatarCanvas(index: number, scale = 4): HTMLCanvasElement {
  const grid = SPRITES.player;
  const i = ((Math.floor(index) % AVATAR_COUNT) + AVATAR_COUNT) % AVATAR_COUNT;
  const [light, dark] = AVATAR_COLORS[i];
  const rows = grid.length;
  let cols = 0;
  for (const r of grid) cols = Math.max(cols, r.length);
  const c = document.createElement('canvas');
  c.width = cols * scale;
  c.height = rows * scale;
  const ctx = c.getContext('2d');
  if (ctx) {
    for (let y = 0; y < rows; y++) {
      const row = grid[y];
      for (let x = 0; x < row.length; x++) {
        const ch = row[x];
        const col = ch === 'u' ? light : ch === 'U' ? dark : PAL[ch];
        if (col !== undefined && col !== T) {
          ctx.fillStyle = `#${col.toString(16).padStart(6, '0')}`;
          ctx.fillRect(x * scale, y * scale, scale, scale);
        }
      }
    }
  }
  c.style.width = `${cols * scale}px`;
  c.style.height = `${rows * scale}px`;
  c.style.imageRendering = 'pixelated';
  return c;
}

/** Draw a named sprite into a crisp DOM canvas (for the bestiary / menus). */
export function spriteToCanvas(key: string, scale = 3): HTMLCanvasElement {
  const set = activeSprites();
  const grid = set[key] ?? set.slime;
  const rows = grid.length;
  let cols = 0;
  for (const r of grid) cols = Math.max(cols, r.length);
  const c = document.createElement('canvas');
  c.width = cols * scale;
  c.height = rows * scale;
  const ctx = c.getContext('2d');
  if (ctx) {
    for (let y = 0; y < rows; y++) {
      const row = grid[y];
      for (let x = 0; x < row.length; x++) {
        const col = PAL[row[x]];
        if (col !== undefined && col !== T) {
          ctx.fillStyle = `#${col.toString(16).padStart(6, '0')}`;
          ctx.fillRect(x * scale, y * scale, scale, scale);
        }
      }
    }
  }
  c.style.width = `${cols * scale}px`;
  c.style.height = `${rows * scale}px`;
  c.style.imageRendering = 'pixelated';
  return c;
}
